/**
 * Finite physical sources worked from an adjacent camp: single trees and
 * large, widely spaced mineral outcrops. Quarry and mine reserves last many
 * shifts; workers debit them while cutting and deliver each real basket.
 * Leylines remain permanent terrain for the Wellspring's separate economy.
 * Kind IDs are stable; physical widths are saved on individual nodes so old
 * settlements retain their original clearances when loading earlier saves.
 */

import { Job, Terrain, type ResourceKey } from '../sim/types.ts';

export const NodeKind = {
  Tree: 0,
  Rock: 1,
  Ore: 2,
} as const;
export type NodeKindId = (typeof NodeKind)[keyof typeof NodeKind];

export interface NodeKindDef {
  kind: NodeKindId;
  id: string;
  /** Singular, for a tooltip: "Deadwood", "Boulder", "Ore Seam". */
  name: string;
  /** The terrain this kind is seeded from. */
  terrain: number;
  /** What the ground becomes when the last of it is taken. */
  revertTo: number;
  /** What a load off it becomes in the stores. */
  resource: ResourceKey;
  /** The job of the building that works it. See `harvestKind`. */
  job: number;
  /** How much one node holds, in resource units, before any multiplier. */
  amount: number;
  /** Odd tile width, centred on the saved anchor tile. Trees occupy one tile. */
  footprint: number;
  /**
   * One node per `lattice`×`lattice` block of matching terrain.
   *
   * The whole of "more numerous" — a smaller number is a thicker scatter. It is
   * a lattice rather than a per-tile chance so that seeding is a pure walk of
   * the terrain with no rolls in it: two worlds with the same map have the same
   * trees, in the same order, whatever the RNG has been used for elsewhere.
   */
  lattice: number;
  /**
   * How much a worker carries in one trip.
   *
   * The load is what makes distance matter, and it matters *inversely*: the
   * round trip costs the same seconds whatever is in the basket, so a small load
   * means more trips and a heavier tax on a badly placed camp. Ore is the
   * smallest load in the game for the same reason its nodes are the poorest.
   */
  load: number;
}

export const NODE_KINDS: NodeKindDef[] = [
  {
    kind: NodeKind.Tree,
    id: 'tree',
    name: 'Deadwood',
    terrain: Terrain.Forest,
    revertTo: Terrain.AshPlain,
    resource: 'timber',
    job: Job.Woodcutter,
    amount: 45,
    footprint: 1,
    lattice: 3,
    load: 3,
  },
  {
    kind: NodeKind.Rock,
    id: 'rock',
    name: 'Outcrop',
    terrain: Terrain.StoneOutcrop,
    revertTo: Terrain.AshPlain,
    resource: 'stone',
    job: Job.Quarrier,
    /** One coherent outcrop holds roughly half an hour of a basic quarry shift. */
    amount: 900,
    footprint: 3,
    lattice: 14,
    load: 2,
  },
  {
    kind: NodeKind.Ore,
    id: 'ore',
    name: 'Ore Seam',
    terrain: Terrain.IronSeam,
    revertTo: Terrain.AshPlain,
    resource: 'iron',
    job: Job.Miner,
    /** Ore is rarer still, with a long-lived reserve and small carried loads. */
    amount: 750,
    footprint: 3,
    lattice: 16,
    load: 1.5,
  },
];

export function nodeKind(kind: number): NodeKindDef {
  return NODE_KINDS[kind] ?? NODE_KINDS[0];
}

/** The kind seeded from this terrain, or −1. */
export function nodeKindForTerrain(terrain: number): number {
  for (const k of NODE_KINDS) if (k.terrain === terrain) return k.kind;
  return -1;
}

/**
 * The kind a building with this job works, or −1.
 *
 * Keyed on the **job** rather than on `needsTerrain`, and the difference is a
 * real one rather than tidiness. The Verdant Choir's Sunken Grove also stands on
 * `Terrain.Forest` and also produces timber, and it is a *grove*: it grows, it
 * is the Choir's housing, and a grove that felled the wood it was grown in would
 * eat its own people's beds. Its job is `Farmer`, so it falls through here and
 * keeps producing exactly as it always did. Only the four buildings whose job is
 * Woodcutter, Quarrier or Miner haul.
 */
export function harvestKind(job: number): number {
  for (const k of NODE_KINDS) if (k.job === job) return k.kind;
  return -1;
}

export function nodeKindById(id: string): number {
  for (const k of NODE_KINDS) if (k.id === id) return k.kind;
  return -1;
}
